import React, { useState } from "react";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";

const waitForElement = async (doc, domId, timeout = 15000) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
        const el = doc.querySelector(domId);
        if (el && el.innerHTML.trim().length > 50) {
            await new Promise(r => setTimeout(r, 1500));
            const finalEl = doc.querySelector(domId);
            if (finalEl) return finalEl;
        }
        await new Promise(r => setTimeout(r, 400));
    }
    throw new Error(`Timeout waiting for ${domId} to load data`);
};

const RatingExportModal = ({ isOpen, onClose, ratingId }) => {
    const { t } = useTranslation();
    const [loadingKey, setLoadingKey] = useState(null);
    const [isLandscape, setIsLandscape] = useState(false);

    const handleExport = async (exportType) => {
        if (loadingKey) return;
        setLoadingKey(exportType);

        try {
            const iframeUrl = `${window.location.origin}/admin/ratings/${ratingId}`;

            const iframe = document.createElement("iframe");
            iframe.src = iframeUrl;
            iframe.style.cssText = `
                position: fixed;
                top: -9999px;
                left: -9999px;
                width: 1500px;
                height: 25000px;
                border: none;
                z-index: -999;
                pointer-events: none;
                visibility: hidden;
            `;
            document.body.appendChild(iframe);

            await new Promise(resolve => { iframe.onload = resolve; });

            // We look for the rating table wrapper
            const element = await waitForElement(iframe.contentDocument, '.rating-table-wrapper');

            const initialWidth = Math.max(element.scrollWidth, 1200);
            const initialHeight = Math.max(element.scrollHeight, 1500);

            // Force light theme AFTER React has mounted
            iframe.contentDocument.documentElement.setAttribute('data-theme', 'light');
            iframe.contentDocument.body.setAttribute('data-theme', 'light');
            
            // Explicitly set element background to white to prevent transparent PDFs in dark-mode viewers
            element.style.backgroundColor = '#ffffff';

            // Inject local CSS variables to completely override any dark theme leakage from the main window
            const lightStyle = iframe.contentDocument.createElement('style');
            lightStyle.innerHTML = `
                .rating-table-wrapper {
                    --easy-black: #2a2d36 !important;
                    --light-black: #263238 !important;
                    --dark-color: #5d586c !important;
                    --primary-color: #595664 !important;
                    --secondary-color: #a5a3ae !important;
                    --grey-blue: #85919d !important;
                    --white-blue: #e5e9ea !important;
                    --light-blue: #d8e3eb !important;
                    --light-color: #d1d0d5 !important;
                    --classic-grey: #eee !important;
                    --white-gray: #f3f3f3 !important;
                    --body-background: #f8f7fa !important;
                    --white: #fff !important;
                    --super-white: #fdfdfd !important;
                    --text: #1f2937 !important;
                    --line: #e3e3e3 !important;
                    color: #1f2937 !important;
                }
            `;
            element.appendChild(lightStyle);

            // Prevent React from re-applying dark theme during async export
            const observer = new MutationObserver(() => {
                if (iframe.contentDocument.documentElement.getAttribute('data-theme') === 'dark') {
                    iframe.contentDocument.documentElement.setAttribute('data-theme', 'light');
                }
            });
            observer.observe(iframe.contentDocument.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

            iframe.style.width = `${initialWidth + 200}px`;
            iframe.style.height = `${initialHeight + 200}px`;

            await new Promise(r => setTimeout(r, 200));

            const exportWidth = element.scrollWidth;
            const exportHeight = element.scrollHeight;

            const uppercaseElements = element.querySelectorAll('.uppercase0');
            uppercaseElements.forEach(el => {
                const text = el.textContent?.trim();
                if (text) {
                    el.textContent = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                    el.style.textTransform = 'none';
                }
            });

            // Clean up avatar placeholders for export (html2canvas sometimes misaligns SVGs)
            const noThumbIcons = element.querySelectorAll('.no-thumbnail svg');
            noThumbIcons.forEach(icon => {
                icon.style.display = 'none';
            });

            const originalPadding = element.style.padding;
            const originalBoxSizing = element.style.boxSizing;
            const originalOverflow = element.style.overflow;
            const originalHeight = element.style.height;

            element.style.padding = "20px";
            element.style.boxSizing = "border-box";
            element.style.overflow = "visible";
            element.style.height = "auto";

            const filename = `Rating_${ratingId}`;

            if (exportType === 'csv') {
                const rows = Array.from(element.querySelectorAll('tr'));
                const csvContent = rows.map(row => {
                    const cells = Array.from(row.querySelectorAll('th, td'));
                    return cells.map(cell => {
                        let text = cell.innerText.replace(/"/g, '""').replace(/\n/g, ' ').trim();
                        return `"${text}"`;
                    }).join(",");
                }).join("\n");

                const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${filename}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

            } else if (exportType === 'pdf') {
                // Prevent page breaks inside rows
                const trs = element.querySelectorAll('tr');
                trs.forEach(tr => {
                    tr.style.pageBreakInside = 'avoid';
                });

                const opt = {
                    margin:       10,
                    filename:     `${filename}.pdf`,
                    image:        { type: 'jpeg', quality: 0.98 },
                    html2canvas:  {
                        scale: 1.5,
                        useCORS: true,
                        logging: false,
                        windowWidth: exportWidth,
                        windowHeight: exportHeight,
                        backgroundColor: '#ffffff'
                    },
                    jsPDF: {
                        unit: 'mm',
                        format: 'a4',
                        orientation: isLandscape ? 'landscape' : 'portrait'
                    },
                    pagebreak:    { mode: ['css', 'legacy'] }
                };

                // Dynamic import: html2pdf.js (bundles jspdf+html2canvas
                // internally) is only needed for this one export option -
                // loaded on demand instead of shipping it to every page load.
                const { default: html2pdf } = await import("html2pdf.js");
                await html2pdf().set(opt).from(element).save();
            } else if (exportType === 'png') {
                // Dynamic import - same reasoning as html2pdf.js above.
                const { default: html2canvas } = await import("html2canvas");
                const canvas = await html2canvas(element, {
                    useCORS: true,
                    scale: 2,
                    windowWidth: exportWidth,
                    windowHeight: exportHeight,
                    logging: false,
                    backgroundColor: '#ffffff',
                });

                const link = document.createElement("a");
                link.download = `${filename}.png`;
                link.href = canvas.toDataURL("image/png");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            element.style.padding = originalPadding;
            element.style.boxSizing = originalBoxSizing;
            element.style.overflow = originalOverflow;
            element.style.height = originalHeight;
            element.style.backgroundColor = '';
            element.style.color = '';

            toast.success(t("Export saved!"));
        } catch (err) {
            console.error(err);
            toast.error(t("Export failed"));
        } finally {
            const iframe = document.querySelector("iframe[style*='-9999px']");
            if (iframe) document.body.removeChild(iframe);
            setLoadingKey(null);
        }
    };

    return (
        <CustomModal isOpen={isOpen} onClose={onClose}>
            <form className="modal-form">
                <h2><FeatherIcon icon="download" /> {t("Export rating")}</h2>

                <div className="admin-list admin-share">
                    
                    {/* CSV */}
                    <div
                        className={`admin-card ${loadingKey === 'csv' ? "selected" : "editable"}`}
                        style={{ cursor: loadingKey ? "wait" : "pointer", padding: "10px", gridTemplateColumns: "20px 1fr auto" }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!loadingKey) handleExport('csv');
                        }}
                    >
                        <div><FeatherIcon icon="file-text" size={16} /></div>
                        <div>CSV</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: "auto" }}>
                            {loadingKey === 'csv' ? <FeatherIcon icon="loader" className="spin" size={16} /> : <FeatherIcon icon="download" size={16} />}
                        </div>
                    </div>

                    {/* PDF */}
                    <div
                        className={`admin-card ${loadingKey === 'pdf' ? "selected" : "editable"}`}
                        style={{ cursor: loadingKey ? "wait" : "pointer", padding: "10px", gridTemplateColumns: "20px 1fr auto" }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!loadingKey) handleExport('pdf');
                        }}
                    >
                        <div><FeatherIcon icon="file" size={16} /></div>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <span>PDF</span>
                            <div 
                                onClick={e => e.stopPropagation()} 
                                style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", background: "var(--body-background)", padding: "2px 8px", borderRadius: "10px" }}
                            >
                                <input 
                                    type="checkbox" 
                                    id="landscapeCheckPdf" 
                                    checked={isLandscape} 
                                    onChange={e => setIsLandscape(e.target.checked)} 
                                />
                                <label htmlFor="landscapeCheckPdf" style={{margin: 0, cursor: "pointer"}}>{t("Horizontal")}</label>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: "auto" }}>
                            {loadingKey === 'pdf' ? <FeatherIcon icon="loader" className="spin" size={16} /> : <FeatherIcon icon="download" size={16} />}
                        </div>
                    </div>

                    {/* PNG */}
                    <div
                        className={`admin-card ${loadingKey === 'png' ? "selected" : "editable"}`}
                        style={{ cursor: loadingKey ? "wait" : "pointer", padding: "10px", gridTemplateColumns: "20px 1fr auto" }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!loadingKey) handleExport('png');
                        }}
                    >
                        <div><FeatherIcon icon="image" size={16} /></div>
                        <div>PNG</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: "auto" }}>
                            {loadingKey === 'png' ? <FeatherIcon icon="loader" className="spin" size={16} /> : <FeatherIcon icon="download" size={16} />}
                        </div>
                    </div>

                </div>
            </form>
        </CustomModal>
    );
};

export default RatingExportModal;
